import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="offers--process-documents"
// This controller is only for process-document section of policy journey where we requires to send selected checkbox ids as a param to each action.
export default class extends Controller {
  static values = {
    onboarding: { type: Boolean, default: false },
  }

  static targets = ["signIcon", "signSpinner", "signLabel", "startSignButton", "missingSigningChoiceWarning"]

  connect() {
    this.updateSigningChoiceState()
    this.element.addEventListener("change", this.onDocumentCheckboxChange)
  }

  disconnect() {
    this.element.removeEventListener("change", this.onDocumentCheckboxChange)
  }

  onDocumentCheckboxChange = (event) => {
    if (event.target.matches('input[type="checkbox"][name="document_ids[]"]')) {
      this.updateSigningChoiceState()
    }
  }

  // Scoped to name="document_ids[]" so decorative, non-selectable checkboxes elsewhere on the
  // page (e.g. the locked "required document" indicator) aren't picked up as selected documents.
  get checkBoxes() {
    if (this.onboardingValue) {
      const rootElement = document.querySelector('[data-controller="checkbox-selection"]')
      return Array.from(rootElement.querySelectorAll('input[type="checkbox"][name="document_ids[]"]'));
    } else {
      return Array.from(this.element.querySelectorAll('input[type="checkbox"][name="document_ids[]"]'));
    }
  }

  // LV1871's Antrag/Kurzantrag pair: exactly one of them needs to stay checked.
  get signingChoiceCheckBoxes() {
    return this.checkBoxes.filter((checkbox) => checkbox.dataset.exclusiveSigningChoice === "true")
  }

  hasValidSigningChoice() {
    const relevant = this.signingChoiceCheckBoxes
    return relevant.length === 0 || relevant.some((checkbox) => checkbox.checked)
  }

  updateSigningChoiceState() {
    const valid = this.hasValidSigningChoice()

    this.missingSigningChoiceWarningTargets.forEach((warning) => warning.classList.toggle("hidden", valid))
    this.startSignButtonTargets.forEach((button) => {
      button.disabled = button.dataset.baseDisabled === "true" || !valid
      button.classList.toggle("opacity-50", button.disabled)
      button.classList.toggle("grayscale", button.disabled)
    })
  }

  async downloadDocuments(event) {
    event.preventDefault();

    const selectedIds = this.checkBoxes.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.value);

    if (selectedIds.length > 0) {
      const params = new URLSearchParams(selectedIds.map(id => `document_ids[]=${id}`).join('&'));
      const url = `${event.currentTarget.href}?${params}`;
      window.location.href = url;
    }
  }

  async appendParamsandSubmitRequest(event) {
    event.preventDefault();

    const button = event.currentTarget
    if (button.disabled || !this.hasValidSigningChoice()) return

    this.setSignButtonLoading(button, true)

    const form = event.target.closest("form")
    const url = new URL(form.action)

    const selectedIds = this.checkBoxes.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.dataset.unsignedDocumentId)
    selectedIds.forEach(id => url.searchParams.append('document_ids[]', id))
    form.action = url.toString()

    form.addEventListener("turbo:submit-end", () => this.setSignButtonLoading(button, false), { once: true })
    form.requestSubmit()
  }

  setSignButtonLoading(button, loading) {
    button.disabled = loading
    button.classList.toggle("opacity-50", loading)
    button.classList.toggle("grayscale", loading)
    button.classList.toggle("cursor-not-allowed", loading)
    button.classList.toggle("cursor-pointer", !loading)

    if (this.hasSignIconTarget) this.signIconTarget.classList.toggle("hidden", loading)
    if (this.hasSignSpinnerTarget) this.signSpinnerTarget.classList.toggle("hidden", !loading)

    if (this.hasSignLabelTarget) {
      if (loading) {
        this.signLabelOriginalText ??= this.signLabelTarget.textContent
        this.signLabelTarget.textContent = button.dataset.loadingLabel
      } else if (this.signLabelOriginalText) {
        this.signLabelTarget.textContent = this.signLabelOriginalText
      }
    }
  }
}
